var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from '@reduxjs/toolkit';
import i18next from 'i18next';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import times from 'lodash/times';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PlanType, PlanTypeCategory } from '../../enum';
import { useAPI } from '../../state';
import { formatDateToLocaleUppercase } from '../../util';
import { sortByLOBName } from '../common';
import { useDownload } from '../hooks';
import { QueueType } from '../multiskill/types';
import { ActionType } from '../report/constants';
import { useReportDownload } from '../report/hooks';
import { useReportAPI, useReportPlanType } from '../report/selectors';
import { PLAN_COLORS } from '../wizard/constants';
import { createTimelineSelector, selectTimelineOptions } from '../wizard/selectors';
import { appliesPatienceRetryFields } from '../wizard/workVolume/patienceRetry';
import { METRICS } from './common';
import { ALL_DAILY_FILTER_VALUE, UNASSIGNED_DAILY_LOB_VALUE, UNASSIGNED_DAILY_SUB_LOB_VALUE, useDailyContext } from './context';
var WAP_QUEUES_API = '/api/queue/wap/{basePlanId}';
var DAILY_REPORT_DETAIL_API = '/api/report/daily';
var DAILY_REPORT_AGGREGATE_API = '/api/report/daily/aggregate';
var DAILY_REPORT_CHART_AGGREGATE_API = '/api/report/daily/chart/aggregate';
var PRINT_API = '/api/report/daily/print';
var EXPORT_API = '/api/report/daily/export';
var EXPORT_FILE_NAMES = {
  daily: 'Forecast_Daily',
  interval: 'Forecast_Interval'
};
export var isDailyQueueData = function (data) {
  return !!data && typeof data === 'object' && 'times' in data;
};
export var isDailyQueuesData = function (data) {
  return !!data && typeof data === 'object' && !('times' in data);
};
var selectDailyData = function (data) {
  return data;
};
var selectDailySummary = createSelector(selectDailyData, function (data) {
  return data === null || data === void 0 ? void 0 : data.summary;
});
export var selectTimes = createSelector(selectDailyData, function (data) {
  var _a;
  if (!data) return undefined;
  if (isDailyQueueData(data)) return data.times;
  return (_a = Object.values(data)[0]) === null || _a === void 0 ? void 0 : _a.times;
});
var selectQueueNames = createSelector(selectDailyData, function (data) {
  if (!data) return [];
  if (isDailyQueueData(data)) {
    return [{
      queueName: data.queueName,
      queueType: data.queueType,
      isAbandonable: data.isAbandonable
    }];
  }
  return Object.values(data).map(function (_a) {
    var queueName = _a.queueName,
      queueType = _a.queueType,
      isAbandonable = _a.isAbandonable;
    return {
      queueName: queueName,
      queueType: queueType,
      isAbandonable: isAbandonable
    };
  });
});
var transformData = function (data) {
  if (!data || 'times' in data) return data === null || data === void 0 ? void 0 : data.detail;
  return Object.keys(data).reduce(function (acc, queueId) {
    var _a = data[queueId],
      queueName = _a.queueName,
      queueType = _a.queueType,
      detail = _a.detail;
    Object.keys(detail).forEach(function (day) {
      acc[day] = acc[day] || {};
      Object.keys(detail[day]).forEach(function (time) {
        acc[day][time] = acc[day][time] || [];
        acc[day][time].push({
          queueName: queueName,
          queueType: queueType,
          detail: detail[day][time]
        });
      });
    });
    return acc;
  }, {});
};
export var selectDailyDataTransformed = createSelector(selectDailyData, function (data) {
  return transformData(data);
});
var selectQueues = function (queues) {
  return Array.isArray(queues) ? queues : [];
};
var selectQueueMap = createSelector(selectQueues, function (queues) {
  return keyBy(queues, 'id');
});
var selectQueuesGroup = createSelector(selectQueues, function (queues) {
  return mapValues(groupBy(queues, 'planId'), function (children) {
    return {
      queues: children,
      byName: keyBy(children, 'name')
    };
  });
});
var createUniqueQueuesSelector = function (uniqueId) {
  return createSelector(selectQueuesGroup, selectQueues, function (group, queues) {
    var _a;
    if (!(queues === null || queues === void 0 ? void 0 : queues.length)) return [];
    var planId = uniqueId ? uniqueId === null || uniqueId === void 0 ? void 0 : uniqueId.split('-')[0] : queues[0].planId;
    var queueList = (_a = group[planId]) === null || _a === void 0 ? void 0 : _a.queues;
    if (!queueList) return [];
    return (uniqueId === null || uniqueId === void 0 ? void 0 : uniqueId.includes('-')) ? queueList : queueList.filter(function (q) {
      return q.queueType === QueueType.SingleSkill;
    });
  });
};
var createQueueSelector = function (id) {
  if (id === void 0) {
    id = 0;
  }
  return createSelector(selectQueueMap, function (map) {
    return map[id];
  });
};
var createNextQueueSelector = function (id, planId) {
  if (id === void 0) {
    id = 0;
  }
  if (planId === void 0) {
    planId = 0;
  }
  return createSelector(selectQueuesGroup, selectQueueMap, function (group, map) {
    var _a, _b, _c;
    return (_c = (_a = group[planId]) === null || _a === void 0 ? void 0 : _a.byName[(_b = map[id]) === null || _b === void 0 ? void 0 : _b.name]) === null || _c === void 0 ? void 0 : _c.id;
  });
};
export var selectDailyFilterQueues = function (queues, planId) {
  var _a;
  var availableQueues = Array.isArray(queues) ? queues : [];
  var selectedPlanId = planId !== null && planId !== void 0 ? planId : (_a = availableQueues[0]) === null || _a === void 0 ? void 0 : _a.planId;
  // WAP-enabled multiskill group queues are included so the daily/interval view
  // can show their sim rows. Gating lives in the backend (`findWAPQueues` only
  // returns queues with WAP data); the earlier Multiskill exclusion hid groups.
  return availableQueues.filter(function (queue) {
    return queue.planId === selectedPlanId;
  });
};
var createBaselineQueuesSelector = function (uniqueId) {
  return createSelector(selectQueues, function (queues) {
    var selectedPlanId = uniqueId ? Number(uniqueId.split('-')[0]) : undefined;
    var planQueues = selectDailyFilterQueues(queues, selectedPlanId);
    return (uniqueId === null || uniqueId === void 0 ? void 0 : uniqueId.includes('-')) ? planQueues : planQueues.filter(function (queue) {
      return queue.queueType === QueueType.SingleSkill;
    });
  });
};
var selectScenarios = function (plan) {
  return plan === null || plan === void 0 ? void 0 : plan.scenarios;
};
var selectScenarioMap = createSelector(selectScenarios, function (scenario) {
  return keyBy(scenario, 'id');
});
var createScenarioSelector = function (id) {
  if (id === void 0) {
    id = 0;
  }
  return createSelector(selectScenarioMap, function (map) {
    return map[id];
  });
};
var createDailyScenarioSelector = function () {
  return function (plan, queues) {
    var _a;
    var multiskillPlanIds = new Set((queues !== null && queues !== void 0 ? queues : []).filter(function (queue) {
      return queue.queueType === QueueType.Multiskill;
    }).map(function (queue) {
      return queue.planId;
    }));
    return (_a = selectScenarios(plan)) === null || _a === void 0 ? void 0 : _a.flatMap(function (scenario) {
      var name = scenario.isBaseline ? i18next.t('translation:baseline') : scenario.name;
      var singleSkillPlan = __assign(__assign({}, scenario), {
        uniqueId: "".concat(scenario.id),
        name: name
      });
      if (!multiskillPlanIds.has(scenario.id)) return [singleSkillPlan];
      return [singleSkillPlan, __assign(__assign({}, singleSkillPlan), {
        uniqueId: "".concat(scenario.id, "-").concat(QueueType.Multiskill),
        name: "".concat(name, " ").concat(i18next.t('translation:multiskill'))
      })];
    });
  };
};
export var useWAPQueuesAPI = function () {
  var basePlanId = useParams().basePlanId;
  return useAPI(WAP_QUEUES_API, {
    basePlanId: basePlanId
  });
};
export var useUniqueQueues = function (uniqueId) {
  var data = useWAPQueuesAPI()[0].data;
  var selectUniqueQueues = useMemo(function () {
    return createUniqueQueuesSelector(uniqueId);
  }, [uniqueId]);
  return selectUniqueQueues(data);
};
export var useBaselineQueues = function (uniqueId) {
  var _a;
  var basePlanId = useParams().basePlanId;
  var state = useDailyContext()[0];
  var data = useWAPQueuesAPI()[0].data;
  var selectedUniqueId = (_a = uniqueId !== null && uniqueId !== void 0 ? uniqueId : state === null || state === void 0 ? void 0 : state.uniqueId) !== null && _a !== void 0 ? _a : basePlanId;
  var selectBaselineQueues = useMemo(function () {
    return createBaselineQueuesSelector(selectedUniqueId);
  }, [selectedUniqueId]);
  return selectBaselineQueues(data);
};
export var useQueueById = function (id) {
  var data = useWAPQueuesAPI()[0].data;
  var selectQueue = useMemo(function () {
    return createQueueSelector(id);
  }, [id]);
  return selectQueue(data);
};
export var useNextQueueId = function (queueId, uniqueId) {
  var data = useWAPQueuesAPI()[0].data;
  var selectNextQueueId = useMemo(function () {
    return createNextQueueSelector(queueId, Number(uniqueId === null || uniqueId === void 0 ? void 0 : uniqueId.split('-')[0]));
  }, [uniqueId, queueId]);
  return selectNextQueueId(data);
};
export var usePlanColor = function () {
  var _a;
  var data = useReportAPI()[0].data;
  var uniqueId = useDailyContext('uniqueId')[0];
  var selectScenario = useMemo(function () {
    return createScenarioSelector(Number(uniqueId === null || uniqueId === void 0 ? void 0 : uniqueId.split('-')[0]));
  }, [uniqueId]);
  var plan = selectScenario(data);
  return PLAN_COLORS[(_a = plan === null || plan === void 0 ? void 0 : plan.colorIndex) !== null && _a !== void 0 ? _a : 0];
};
export var useDailyReportPlan = function () {
  var data = useReportAPI()[0].data;
  var queues = useWAPQueuesAPI()[0].data;
  var selectDailyScenario = useMemo(function () {
    return createDailyScenarioSelector();
  }, []);
  return selectDailyScenario(data, queues);
};
export var useReportTimeline = function (includeYear, includeWeek, includeWeekDate) {
  if (includeYear === void 0) {
    includeYear = false;
  }
  if (includeWeek === void 0) {
    includeWeek = false;
  }
  if (includeWeekDate === void 0) {
    includeWeekDate = false;
  }
  var addColumns = [];
  for (var _i = 3; _i < arguments.length; _i++) {
    addColumns[_i - 3] = arguments[_i];
  }
  var data = useReportAPI()[0].data;
  var selectTimelines = useMemo(function () {
    return createTimelineSelector(includeYear, includeWeek, includeWeekDate, addColumns);
  }, [includeYear, includeWeek, includeWeekDate, addColumns]);
  return selectTimelines(data);
};
export var useReportTimelineOptions = function () {
  var data = useReportAPI()[0].data;
  return selectTimelineOptions(data);
};
export var useProdHoursTimelineOptions = function () {
  var data = useReportAPI()[0].data;
  return useMemo(function () {
    if ((data === null || data === void 0 ? void 0 : data.planTypeCategory) !== PlanTypeCategory.ProdHours) {
      return [];
    }
    var startDate = data.startDate,
      periodLength = data.periodLength,
      planType = data.planType;
    if (!startDate || !periodLength) {
      return [];
    }
    var periods = times(periodLength, function (n) {
      return moment.utc(startDate).add(n, planType === PlanType.Weekly ? 'w' : 'M');
    });
    var dateFormat = i18next.t('dateFormat.dayMonthYearFormat', {
      defaultValue: ''
    });
    var isMonthly = planType === PlanType.Monthly || planType === PlanType.MonthlyCustom;
    var periodLabel = isMonthly ? i18next.t('translation:month', {
      defaultValue: 'MONTH'
    }) : i18next.t('translation:week', {
      defaultValue: 'WEEK'
    });
    return periods.map(function (period, id) {
      return {
        id: id,
        name: "".concat(periodLabel, " ").concat(id + 1, " (").concat(period.format(dateFormat || 'DD-MMM-YY').toUpperCase(), ")")
      };
    });
  }, [data]);
};
export var useDailyAPI = function () {
  var _a, _b;
  var state = useDailyContext()[0];
  var reportData = useReportAPI()[0].data;
  var detailAPI = useAPI(DAILY_REPORT_DETAIL_API);
  var aggregateAPI = useAPI(DAILY_REPORT_AGGREGATE_API);
  var uniqueId = state === null || state === void 0 ? void 0 : state.uniqueId;
  var chartQueueIds = state === null || state === void 0 ? void 0 : state.chartQueueIds;
  var chartQueueIdsParam = chartQueueIds === null || chartQueueIds === void 0 ? void 0 : chartQueueIds.join(',');
  var isProdHoursPlan = (reportData === null || reportData === void 0 ? void 0 : reportData.planTypeCategory) === PlanTypeCategory.ProdHours;
  var useAggregateAPI = !isProdHoursPlan || !!(chartQueueIds === null || chartQueueIds === void 0 ? void 0 : chartQueueIds.length);
  var _c = useAggregateAPI ? aggregateAPI : detailAPI,
    data = _c[0],
    fetchData = _c[1],
    clearData = _c[2];
  var selectedProdHoursQueueId = typeof (state === null || state === void 0 ? void 0 : state.chartQueueId) === 'number' ? state.chartQueueId : (_a = chartQueueIds === null || chartQueueIds === void 0 ? void 0 : chartQueueIds[0]) !== null && _a !== void 0 ? _a : state === null || state === void 0 ? void 0 : state.queueId;
  var availableQueues = useBaselineQueues(uniqueId);
  var hasValidQueueSelection = useAggregateAPI ? !!(chartQueueIds === null || chartQueueIds === void 0 ? void 0 : chartQueueIds.length) && chartQueueIds.every(function (id) {
    return availableQueues.some(function (queue) {
      return queue.id === id;
    });
  }) : typeof selectedProdHoursQueueId === 'number' && availableQueues.some(function (queue) {
    return queue.id === selectedProdHoursQueueId;
  });
  var month = state === null || state === void 0 ? void 0 : state.month;
  var viewType = (_b = state === null || state === void 0 ? void 0 : state.viewType) !== null && _b !== void 0 ? _b : 'daily';
  var fetchDataWrapper = useCallback(function () {
    if (uniqueId && hasValidQueueSelection) {
      var dataType = uniqueId.includes('-') ? QueueType.Multiskill : QueueType.SingleSkill;
      if (useAggregateAPI) {
        if (!(chartQueueIds === null || chartQueueIds === void 0 ? void 0 : chartQueueIds.length)) return;
        return fetchData(undefined, {
          planId: uniqueId.split('-')[0],
          queuesId: chartQueueIdsParam,
          month: month,
          dataType: dataType,
          viewType: viewType
        });
      }
      return fetchData(undefined, {
        queueId: selectedProdHoursQueueId,
        month: month,
        dataType: dataType
      });
    }
  }, [chartQueueIds, chartQueueIdsParam, fetchData, hasValidQueueSelection, month, selectedProdHoursQueueId, uniqueId, useAggregateAPI, viewType]);
  return [data, fetchDataWrapper, clearData];
};
export var useDailyChartAPI = function () {
  var _a, _b, _c;
  var state = useDailyContext()[0];
  var reportData = useReportAPI()[0].data;
  var _d = useAPI(DAILY_REPORT_CHART_AGGREGATE_API),
    data = _d[0],
    fetchData = _d[1],
    clearData = _d[2];
  var chartQueueIds = state === null || state === void 0 ? void 0 : state.chartQueueIds;
  var chartQueueIdsParam = chartQueueIds === null || chartQueueIds === void 0 ? void 0 : chartQueueIds.join(',');
  var uniqueId = state === null || state === void 0 ? void 0 : state.uniqueId;
  var viewType = (state === null || state === void 0 ? void 0 : state.viewType) || 'daily';
  var month = state === null || state === void 0 ? void 0 : state.month;
  var startMonth = (_b = (_a = state === null || state === void 0 ? void 0 : state.startMonth) !== null && _a !== void 0 ? _a : month) !== null && _b !== void 0 ? _b : 0;
  var endMonth = (reportData === null || reportData === void 0 ? void 0 : reportData.planType) === PlanType.Weekly ? Math.max((_c = state === null || state === void 0 ? void 0 : state.endMonth) !== null && _c !== void 0 ? _c : startMonth, startMonth) : startMonth;
  var activeDate = state === null || state === void 0 ? void 0 : state.activeDate;
  var fetchDataWrapper = useCallback(function () {
    if ((chartQueueIds === null || chartQueueIds === void 0 ? void 0 : chartQueueIds.length) && uniqueId) {
      return fetchData(undefined, {
        planId: uniqueId.split('-')[0],
        queuesId: chartQueueIdsParam,
        viewType: viewType,
        month: month,
        startMonth: startMonth,
        endMonth: endMonth,
        selectedDate: activeDate
      });
    }
  }, [activeDate, chartQueueIds, chartQueueIdsParam, endMonth, fetchData, month, startMonth, uniqueId, viewType]);
  return [data, fetchDataWrapper, clearData];
};
var allOption = function (name) {
  return {
    id: ALL_DAILY_FILTER_VALUE,
    name: name
  };
};
var isAllFilter = function (value) {
  return value === ALL_DAILY_FILTER_VALUE;
};
var isUnassignedLobFilter = function (value) {
  return value === UNASSIGNED_DAILY_LOB_VALUE;
};
var isUnassignedSubLobFilter = function (value) {
  return value === UNASSIGNED_DAILY_SUB_LOB_VALUE;
};
var isCombinedLobFilter = function (value) {
  return typeof value === 'string' && value.startsWith('lob:');
};
export var toCombinedLobFilterValue = function (queue) {
  var lobValue = queue.lobName && typeof queue.lobId === 'number' ? queue.lobId : 'uncategorized';
  var subLobValue = queue.subLobName && typeof queue.subLobId === 'number' ? queue.subLobId : 'none';
  return "lob:".concat(lobValue, "|sub:").concat(subLobValue);
};
var getCombinedLobLabel = function (queue, unassignedLob) {
  if (!queue.lobName) return unassignedLob;
  if (!queue.subLobName) return queue.lobName;
  return "".concat(queue.lobName, ": ").concat(queue.subLobName);
};
var matchesCombinedLob = function (queue, value) {
  return toCombinedLobFilterValue(queue) === value;
};
var matchesDailyChartLob = function (queue, lobId) {
  if (isAllFilter(lobId) || lobId === undefined) return true;
  // Multiskill groups have no LOB but must NOT be treated as "Unassigned" and
  // must NOT match any concrete LOB (incl. the `lob:uncategorized` combined key):
  // they only appear under their own "Multi-skill" category when LOB = All.
  if (queue.queueType === QueueType.Multiskill) return false;
  if (isCombinedLobFilter(lobId)) return matchesCombinedLob(queue, lobId);
  if (isUnassignedLobFilter(lobId)) return !queue.lobName;
  return queue.lobId === lobId;
};
var matchesDailyChartSubLob = function (queue, subLobId) {
  if (isAllFilter(subLobId) || subLobId === undefined) return true;
  if (isUnassignedSubLobFilter(subLobId)) return !queue.subLobName;
  return queue.subLobId === subLobId;
};
export var useDailyChartLobs = function (uniqueId) {
  var queues = useBaselineQueues(uniqueId);
  var t = useTranslation('translation').t;
  return useMemo(function () {
    var unassignedLob = t('autocomplete.unassignedLOB');
    // Multiskill groups don't belong to any LOB and must not create an
    // "Unassigned" LOB option - they surface under "Multi-skill" when LOB = All.
    var lobs = queues.filter(function (queue) {
      return queue.queueType !== QueueType.Multiskill;
    }).map(function (q) {
      return {
        id: toCombinedLobFilterValue(q),
        name: getCombinedLobLabel(q, unassignedLob)
      };
    });
    return __spreadArray([allOption(t('selectAll'))], uniqBy(lobs, function (lob) {
      return "".concat(lob.id, "-").concat(lob.name);
    }).sort(function (a, b) {
      return sortByLOBName(__assign(__assign({}, a), {
        lobName: a.name
      }), __assign(__assign({}, b), {
        lobName: b.name
      }));
    }), true);
  }, [queues, t]);
};
export var useDailyChartQueues = function (lobId, subLobId, uniqueId) {
  var queues = useBaselineQueues(uniqueId);
  var t = useTranslation('translation').t;
  return useMemo(function () {
    var unassignedLob = t('autocomplete.unassignedLOB');
    var scopedQueues = queues.filter(function (q) {
      return matchesDailyChartLob(q, lobId) && (isCombinedLobFilter(lobId) || matchesDailyChartSubLob(q, subLobId));
    });
    return __spreadArray([allOption(t('selectAll'))], sortBy(scopedQueues, function (queue) {
      return queue.queueType === QueueType.Multiskill ? 'Multi-skill' : getCombinedLobLabel(queue, unassignedLob);
    }, 'name'), true);
  }, [queues, lobId, subLobId, t]);
};
export var useDailyChartQueueIds = function (lobId, subLobId, queueId, uniqueId) {
  var queues = useBaselineQueues(uniqueId);
  return useMemo(function () {
    if (typeof queueId === 'number') return queues.some(function (queue) {
      return queue.id === queueId;
    }) ? [queueId] : [];
    return queues.filter(function (q) {
      return matchesDailyChartLob(q, lobId) && (isCombinedLobFilter(lobId) || matchesDailyChartSubLob(q, subLobId));
    }).map(function (q) {
      return q.id;
    });
  }, [queues, lobId, subLobId, queueId]);
};
export var useDailyLOBs = function (uniqueId) {
  var queues = useUniqueQueues(uniqueId);
  return useMemo(function () {
    var lobs = queues.map(function (q) {
      return {
        id: q.lobId,
        name: q.lobName || 'Unassigned LOB'
      };
    });
    return uniqBy(lobs, 'id');
  }, [queues]);
};
export var useDailySubLOBs = function (uniqueId, lobId) {
  var queues = useUniqueQueues(uniqueId);
  return useMemo(function () {
    if (lobId === undefined) return [];
    var subLobs = queues.filter(function (q) {
      return q.lobId === lobId && q.subLobId;
    }).map(function (q) {
      return {
        id: q.subLobId,
        lobId: q.lobId,
        name: q.subLobName || 'Unassigned Sub-LOB'
      };
    });
    return uniqBy(subLobs, 'id');
  }, [queues, lobId]);
};
export var useDailyQueues = function (uniqueId, lobId, subLobId) {
  var queues = useUniqueQueues(uniqueId);
  return useMemo(function () {
    if (lobId === undefined) return [];
    return queues.filter(function (q) {
      var matchLob = q.lobId === lobId;
      var matchSubLob = subLobId === undefined || subLobId === null || q.subLobId === subLobId;
      return matchLob && matchSubLob;
    });
  }, [queues, lobId, subLobId]);
};
export var useDailySummaryData = function () {
  var data = useDailyAPI()[0].data;
  return selectDailySummary(data);
};
export var useGetQueueNames = function () {
  var data = useDailyAPI()[0].data;
  return selectQueueNames(data);
};
export var useVisibleMetrics = function () {
  var data = useDailyAPI()[0].data;
  var queueNames = useGetQueueNames();
  var queueId = useDailyContext('queueId')[0];
  var queue = useQueueById(queueId);
  var uniqueId = useDailyContext('uniqueId')[0];
  var hasGroupedQueues = isDailyQueuesData(data);
  var hasMultiskill = (uniqueId === null || uniqueId === void 0 ? void 0 : uniqueId.includes('-')) && hasGroupedQueues;
  var showAbandonmentRate = useMemo(function () {
    var lobQueues = isDailyQueueData(data) && Array.isArray(data.queuesByLob) ? data.queuesByLob.flatMap(function (lob) {
      return lob.queues;
    }) : [];
    if (lobQueues.length) {
      return lobQueues.some(function (q) {
        return q.isAbandonable;
      });
    }
    if (hasGroupedQueues || hasMultiskill) {
      return queueNames === null || queueNames === void 0 ? void 0 : queueNames.some(function (q) {
        return q.isAbandonable;
      });
    }
    return appliesPatienceRetryFields({
      isDeferrable: queue === null || queue === void 0 ? void 0 : queue.isDeferrable,
      worktypeId: queue === null || queue === void 0 ? void 0 : queue.worktypeId,
      metricTarget: queue === null || queue === void 0 ? void 0 : queue.metricTarget
    });
  }, [data, hasGroupedQueues, hasMultiskill, queue, queueNames]);
  return useMemo(function () {
    return showAbandonmentRate ? METRICS : METRICS.filter(function (m) {
      return !m.key.startsWith('abandonment');
    });
  }, [showAbandonmentRate]);
};
export var useExportAPI = function (viewType, url) {
  if (viewType === void 0) {
    viewType = 'daily';
  }
  if (url === void 0) {
    url = EXPORT_API;
  }
  var t = useTranslation(['report'], {
    keyPrefix: 'export'
  }).t;
  var fileName = EXPORT_FILE_NAMES[viewType];
  var _a = useDownload(url, "".concat(t(fileName, {
      defaultValue: fileName
    }), ".xlsx")),
    downloadData = _a[0],
    fetchDownloadData = _a[1],
    clearDownloadData = _a[2];
  return [downloadData, fetchDownloadData, clearDownloadData];
};
export var usePrintAPI = function () {
  var _a;
  var state = useDailyContext()[0];
  var planType = useReportPlanType();
  var params = useMemo(function () {
    return pick(state, 'queueId');
  }, [state]);
  return useReportDownload(ActionType.Print, PRINT_API, "".concat(EXPORT_FILE_NAMES.daily, ".pdf"), {
    disabled: !state || planType !== PlanType.Monthly || ((_a = state === null || state === void 0 ? void 0 : state.uniqueId) === null || _a === void 0 ? void 0 : _a.includes('-')),
    params: params
  });
};
export var useDaysOfWeek = function () {
  var state = useDailyContext()[0];
  var data = useReportAPI()[0].data;
  var i18n = useTranslation('translation').i18n;
  return useMemo(function () {
    var _a;
    if (!(data === null || data === void 0 ? void 0 : data.startDate)) return [];
    var isMonthly = data.planType === PlanType.Monthly || data.planType === PlanType.MonthlyCustom;
    var start = moment.utc(data.startDate).add((_a = state === null || state === void 0 ? void 0 : state.month) !== null && _a !== void 0 ? _a : 0, isMonthly ? 'M' : 'w');
    var next = start.clone().add(1, isMonthly ? 'M' : 'w');
    var dayCount = next.diff(start, 'days');
    var dateFormat = i18next.t('dateFormat.dayMonthYearFormat', {
      defaultValue: 'YY-MMM-DD'
    });
    var language = i18n.language || i18next.language;
    return Array.from({
      length: dayCount
    }, function (__unused, i) {
      var d = start.clone().add(i, 'days').locale(language);
      var formattedDate = formatDateToLocaleUppercase(d.format(dateFormat));
      return {
        id: d.format('YYYY-MM-DD'),
        name: formattedDate,
        label: formattedDate,
        dayName: formatDateToLocaleUppercase(d.format('ddd'))
      };
    });
  }, [data, i18n.language, state === null || state === void 0 ? void 0 : state.month]);
};